'use client';

import { clearInviteNotify } from '@/api/account';
import { CrmPointRule } from '@/api/types/public';
import Dialog from '@/components/Dialog';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CloseOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import React, { useCallback, useContext, useMemo } from 'react';

interface InviteGetPointsProps {
  show: boolean;
  onClose: () => void;
  crmPointRules?: CrmPointRule;
  invitePayPoint: number;
}

const InviteGetPoints: React.FC<InviteGetPointsProps> = ({
  show,
  onClose,
  crmPointRules,
  invitePayPoint,
}) => {
  const router = useRouter();
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const { deviceType } = useContext(ResponsiveContext);
  const t = useTranslations('account.pointsMall.inviteGetPoints');
  const currency = useStore((state) => state.currency);

  const pointDollar = (() => {
    try {
      const ratio = crmPointRules?.deduct?.ratio || 0;
      if (!ratio) return '';
      const num1 = Number(invitePayPoint) / Number(ratio);
      return Math.ceil(num1) || '';
    } catch (error) {
      logger.error('Failed to calculate point dollar:', error);
      return '';
    }
  })();

  const handleClose = useCallback(async () => {
    onClose();
    try {
      await clearInviteNotify({ type: 'invitee_pay' });
      setTimeout(() => {
        refreshAccountInfo();
      }, 100);
    } catch (error) {
      logger.error('Failed to clear invite notify:', error);
    }
  }, [onClose, refreshAccountInfo]);

  const handleShare = useCallback(() => {
    handleClose();
    router.push('/account/refer-a-friend');
  }, [handleClose, router]);

  const backgroundImage = useMemo(() => {
    return `url(/zeelool-next/account/points-mall/points-bg.png) !important`;
  }, []);

  return (
    <Dialog
      open={show}
      onClose={undefined}
      disableEscapeKeyDown
      maxWidth="sm"
      className="!bg-transparent !shadow-none"
      footer={null}
      showCloseIcon={false}
      PaperProps={{
        sx: {
          width: deviceType === EPlatform.WEB ? '414px' : '282px',
          maxWidth: deviceType === EPlatform.WEB ? '90vw' : '80vw',
          height: deviceType === EPlatform.WEB ? '480px' : '354px',
          boxShadow: 'none',
          backgroundImage,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
          '& .MuiDialogContent-root': {
            padding: '0',
          },
          '& .MuiDialogTitle-root': {
            padding: '0',
          },
        },
      }}
    >
      <div
        className={`${deviceType === EPlatform.WEB ? 'pt-[195px]' : 'pt-[140px]'} flex flex-col items-center relative`}
      >
        <div
          className={`absolute  w-[24px] h-[24px] top-[20px] right-0 flex items-center justify-center cursor-pointer text-white border-1 border-white rounded-[50px]`}
        >
          <CloseOutlined
            className="w-[24px] h-[24px] cursor-pointer text-white"
            onClick={handleClose}
          />
        </div>

        <p
          className={`text-center text-[${deviceType === EPlatform.WEB ? '26px' : '18px'}] font-bb`}
        >
          {t('title')}
        </p>
        <p
          className={`text-[50px] leading-[50px] text-[#ff8800] font-bb  ${
            deviceType === EPlatform.WEB ? 'my-[20px]' : 'mt-[15px] mb-[10px]'
          }`}
        >
          {t('pointsEarned', { points: invitePayPoint })}
        </p>
        <p
          className={`text-center ${
            deviceType === EPlatform.WEB
              ? 'text-[19px]  leading-[29px] mb-[29px] px-[25px]'
              : 'text-[13px] leading-[20px] mb-[20px] px-[10px]'
          }`}
        >
          {t('savings', { amount: formatPriceNumberWithSymbol(`${pointDollar}`, currency) })}
        </p>
        <div
          className={` bg-primary-purple rounded-[50px] font-bb text-center text-white cursor-pointer ${
            deviceType === EPlatform.WEB
              ? 'w-[264px] h-[53px] leading-[53px] text-[21px]'
              : 'w-[180px] h-[36px] leading-[36px] text-[14px]'
          }`}
          onClick={handleShare}
        >
          {t('shareButton')}
        </div>
      </div>
    </Dialog>
  );
};

export default InviteGetPoints;
